import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { COMPANY } from '@/shared/config';
import { Wordmark } from '@/shared/ui/logo';

/**
 * 푸터 — 1차 오픈 범위(14 §8).
 *
 * **판매하지 않는 동안은 판매 관련 표시를 걷어낸다.**
 * 통신판매업신고번호와 「통신판매업자로서 판매·배송에 책임을 집니다」 문구를 뺐다 —
 * 신고도 안 됐고 파는 것도 없는데 그렇게 적으면 **사실과 다른 표시**가 된다.
 * 환불정책도 뺐다: 결제가 없으면 환불할 것도 없다. 2차 오픈에 셋 다 되돌린다.
 *
 * 대신 **광고성 정보 수신 동의**를 넣었다. 마케팅 동의를 받기 시작했으므로
 * 그 내용을 확인할 경로가 초기화면에 있어야 한다.
 *
 * 값은 shared/config/company.ts 한 곳에서 온다 — 약관 문서와 갈라지면 안 된다.
 */
const LEGAL_LINKS = [
  { to: '/terms', label: '이용약관', strong: false },
  // 개인정보처리방침은 다른 약관과 구분되게 표시하도록 정해져 있다
  { to: '/privacy', label: '개인정보처리방침', strong: true },
  { to: '/marketing-consent', label: '광고성 정보 수신 동의', strong: false },
] as const;

export function SiteFooter(): ReactElement {
  const rows: [string, string][] = [
    ['상호', COMPANY.legalName],
    ['대표자', COMPANY.ceo],
    ['사업자등록번호', COMPANY.businessNumber],
    ['주소', COMPANY.address],
    ['고객문의', COMPANY.email],
  ];

  return (
    <footer className="mt-24 border-border border-t bg-background">
      <div className="u-container flex flex-col gap-8 py-12">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <Wordmark className="w-20 text-muted-foreground [--autop-logo-accent:currentColor]" />
          <nav aria-label="약관">
            <ul className="flex flex-wrap gap-5 text-[13px]">
              {LEGAL_LINKS.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`hover:underline ${item.strong ? 'font-medium' : ''}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <dl className="grid gap-x-8 gap-y-2 text-[12.5px] text-muted-foreground sm:grid-cols-2">
          {rows.map(([label, value]) => (
            <div key={label} className="flex gap-2">
              <dt className="shrink-0">{label}</dt>
              <dd className="text-foreground/80">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </footer>
  );
}
